// LoginRoute — F9.7 WAVE 2I · deep-link friendly /login and /signup pages.
// FASE I Round-3 · regression fix. Previously wrapped <Landing/> in
// <LandingRootRouter> which, since Phase I, performs
// `window.location.replace("/")` on mount → whenever the user hit `/login`
// or `/register`, the SPA reloaded immediately to `/` SSR and the
// just-opened AuthModal was wiped out before becoming visible.
//
// FASE I Round-4 · race condition fix. The round-3 implementation watched
// `modalOpen` transitions to detect dismissal and triggered a
// `window.location.replace("/")` whenever `openedRef && !modalOpen` →
// but React scheduled `setState({ open: true })` for the NEXT render,
// so Effect B observed `modalOpen === false` in the same frame as
// Effect A and reloaded before the modal had a chance to paint. Fix:
// pass an explicit `onClose` callback to `openModal()` — fires exactly
// once when the user dismisses the modal (ESC/backdrop/X), zero state
// inference.
//
// Routes wired in App.js:
//     /login            → <LoginRoute tab="login" />
//     /login?redirect=X → opens modal, redirects to X after submit
//     /signup           → <LoginRoute tab="signup" />
//     /forgot-password  → <LoginRoute tab="forgot" />
import React, { useEffect, useRef } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useAuthModal } from "@/context/AuthModalContext";
import { useAuth } from "@/context/AuthContext";
import { setAuthIntent } from "@/lib/authIntent";

export default function LoginRoute({ tab = "login" }) {
    const { openModal } = useAuthModal();
    const { user, loading } = useAuth();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect") || params.get("next") || "";
    const prefillEmail = params.get("email") || "";
    const openedRef = useRef(false);

    useEffect(() => {
        if (loading) return;
        if (user) return; // handled by <Navigate /> below
        if (openedRef.current) return;
        if (redirect) {
            setAuthIntent({ action: "redirect_after_login", target: redirect });
        }
        openModal(tab, {
            prefillEmail: prefillEmail || undefined,
            // Explicit dismissal → back to SSR landing via full reload.
            // Fires from AuthModalContext.closeModal(). Hotfix post-login
            // redirect · DISTINGUISH dismissal vs success close: LoginForm
            // sets localStorage `glms_session=1` on success BEFORE calling
            // closeModal. If present → user authenticated · skip the
            // reload to `/` (would abort the SPA navigate to /app/dashboard
            // and leave the user on homepage logged-in · non deterministic
            // race condition). Let LoginRoute re-render with <Navigate to
            // "/app/dashboard" /> and LoginForm's own navigate() finish.
            onClose: () => {
                if (typeof window === "undefined") return;
                let authed = false;
                try {
                    authed = window.localStorage.getItem("glms_session") === "1";
                } catch { /* noop */ }
                if (authed) return;
                window.location.replace("/");
            },
        });
        openedRef.current = true;
        // Only on mount — modal opens once; user can toggle inside.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, user]);

    if (!loading && user) {
        // Phase I Wave 2.5 · default post-login landing is V12.
        const to = redirect || "/app/dashboard";
        return <Navigate to={to} replace data-testid="login-route-redirect" />;
    }

    // Minimal premium-dark backdrop — Resend-style iron canvas with a
    // subtle brand lockup. The AuthModal mounts on top via its global
    // provider (<AuthModalProvider /> in App.js).
    return (
        <div
            data-testid={`login-route-${tab}`}
            className="min-h-screen flex items-center justify-center"
            style={{ background: "var(--color-bg-iron, #0B0B0C)" }}
        >
            <div
                aria-hidden="true"
                className="flex items-center gap-3 opacity-40 select-none pointer-events-none"
                style={{ color: "var(--color-text-primary, #F3F4F6)" }}
            >
                <span
                    className="inline-block h-6 w-6 rounded-full"
                    style={{ background: "var(--color-accent, #00A3FF)" }}
                />
                <span className="text-sm font-semibold tracking-tight">
                    GlobalLeadSuite
                </span>
            </div>
        </div>
    );
}
